import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  Header,
  Loadable,
  TableHead,
  useApiGet,
  useSortableTable,
  varClass,
} from '../shared'
import {
  IRejectionReviewAdvertiserMonth,
  IRejectionReviewAdvertiserResponse,
  TRejectionReviewIssueType,
} from '../shared/types'
import {
  europeanizeNumbers,
  formatDecimal,
  formatDkkLarge,
  formatInteger,
} from './shared'
import {
  IssueTypeBadges,
  OpenMonthBadge,
  RejectionPeriod,
  deriveMaturity,
  formatRejectionDkk,
  formatRejectionPercent,
  formatRejectionReviewMonth,
  groupRejectionPeriods,
} from './RejectionReview'
import { RejectionNetworkLane } from './charts/RejectionNetworkLane'

// Full-page advertiser rejection detail. Replaces the old modal. Mirrors the
// shape of detail.tsx: Header → headline → ControlBar → top grid (status +
// summary) → prose → charts → advanced (full monthly table).

// Colors aligned with triage's FAMILY_COLORS — rejection-family blue for the
// headline metric (sale rate + rejected-volume bars), acute red kept for the
// conv-rate line so the two-line rate chart stays distinguishable. Total
// (non-rejected) bars stay neutral grey.
const SALE_RATE_COLOR = '#4F9EEC'
const CONV_RATE_COLOR = '#E55353'
const TOTAL_BAR_COLOR = '#A6B4C9'
const REJECTED_BAR_COLOR = '#4F9EEC'

const RANGE_OPTIONS: { key: number; label: string }[] = [
  { key: 3, label: '3m' },
  { key: 6, label: '6m' },
  { key: 12, label: '12m' },
  { key: 24, label: '24m' },
]

type Props = {
  advertiserDomain: string
  search?: string
}

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------
type UrlState = {
  months: number | null
  device: string | null
  selectedMonth: string | null
}

const parseUrl = (search: string | undefined): UrlState => {
  if (!search) return { months: null, device: null, selectedMonth: null }
  const p = new URLSearchParams(search)
  const m = p.get('months')
  const months = m != null && !isNaN(Number(m)) ? Number(m) : null
  const device = p.get('device')
  const selectedMonth = p.get('selectedMonth')
  return { months, device, selectedMonth }
}

const buildSearch = (state: UrlState): string => {
  const p = new URLSearchParams()
  if (state.months != null) p.set('months', String(state.months))
  if (state.device) p.set('device', state.device)
  if (state.selectedMonth) p.set('selectedMonth', state.selectedMonth)
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const RejectionDetail = (props: Props) => {
  const { advertiserDomain } = props
  const initialUrl = useRef<UrlState>(parseUrl(props.search)).current

  const [months, setMonths] = useState<number>(initialUrl.months ?? 12)
  const [device, setDevice] = useState<string>(initialUrl.device ?? '')
  // selectedMonth is the "focused" month all charts highlight. Defaults to
  // the row's worst month once data loads (handled below in an effect).
  const [selectedMonth, setSelectedMonth] = useState<string | null>(
    initialUrl.selectedMonth
  )
  // Controlled `<details>` for the advanced section so the StatusBanner
  // "jump to worst" button can force it open.
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false)
  // Ref onto the advanced-section container so we can querySelector across
  // all of its tables (periods + Other months) to find any `tr[data-month=...]`
  // and scroll it into view.
  const advancedContainerRef = useRef<HTMLDivElement | null>(null)

  // Mirror state into URL via replaceState so a refresh keeps the view but
  // back/forward navigation still works through the parent.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const target = `/anomalies/rejection-review/${encodeURIComponent(
      advertiserDomain
    )}${buildSearch({
      months,
      device: device || null,
      selectedMonth: selectedMonth || null,
    })}`
    if (window.location.pathname + window.location.search !== target) {
      window.history.replaceState({}, '', target)
    }
  }, [advertiserDomain, months, device, selectedMonth])

  const params = useMemo(() => {
    const p: Record<string, string | number> = {
      advertiser_domain: advertiserDomain,
      months,
    }
    if (device) p.device = device
    return p
  }, [advertiserDomain, months, device])

  const data = useApiGet<IRejectionReviewAdvertiserResponse>(
    '/anomalies/v2/rejection-review/advertiser',
    params,
    [JSON.stringify(params)]
  )

  const monthsData: IRejectionReviewAdvertiserMonth[] = data?.months || []

  // Advanced "Other months" table sort (client-side, ≤24 rows). Defaults to
  // month desc.
  const monthlySort = useSortableTable({ sort: 'conversionMonth', dir: 'desc' })

  const chartData = useMemo(
    () =>
      monthsData.map((m) => ({
        month: m.conversionMonth,
        saleRate: m.rejectedSaleRate ?? null,
        convRate: m.rejectedConversionRate ?? null,
        totalSale: m.totalSaleDkk ?? 0,
        rejectedSale: m.rejectedSaleDkk ?? 0,
        totalConv: m.totalConversions ?? 0,
        rejectedConv: m.rejectedConversions ?? 0,
        maturity: deriveMaturity(m.daysSinceMonthEnd),
      })),
    [monthsData]
  )

  const summary = useMemo(() => {
    let totalRejectedSale = 0
    let totalRejectedConv = 0
    let totalSale = 0
    let totalConv = 0
    let worstSale: number | null = null
    let worstConv: number | null = null
    let worstMonth: string | null = null
    let flaggedMonths = 0
    for (const m of monthsData) {
      totalRejectedSale += m.rejectedSaleDkk ?? 0
      totalRejectedConv += m.rejectedConversions ?? 0
      totalSale += m.totalSaleDkk ?? 0
      totalConv += m.totalConversions ?? 0
      if (m.rejectedSaleRate != null && (worstSale == null || m.rejectedSaleRate > worstSale)) {
        worstSale = m.rejectedSaleRate
        worstMonth = m.conversionMonth
      }
      if (m.rejectedConversionRate != null && (worstConv == null || m.rejectedConversionRate > worstConv)) {
        worstConv = m.rejectedConversionRate
      }
      if ((m.issueTypes || []).length > 0) flaggedMonths += 1
    }
    return {
      totalRejectedSale,
      totalRejectedConv,
      totalSale,
      totalConv,
      worstSale,
      worstConv,
      worstMonth,
      flaggedMonths,
    }
  }, [monthsData])

  // Union of issue types across the most recent 6 months (matches what an
  // operator would see "right now").
  const headerIssueTypes: TRejectionReviewIssueType[] = useMemo(() => {
    const set = new Set<TRejectionReviewIssueType>()
    const recent = [...monthsData].reverse().slice(0, 6)
    for (const m of recent) {
      for (const t of m.issueTypes || []) set.add(t)
    }
    return Array.from(set)
  }, [monthsData])

  // Most recent past month (any month that's already ended) with valid rate
  // data. Used as the "what's the most recent reliable number?" anchor in
  // the status banner. Backend used to expose a settled-only equivalent; the
  // concept is now "completed" (any past month).
  const latestCompleted = useMemo(
    () =>
      [...monthsData]
        .reverse()
        .find((m) => deriveMaturity(m.daysSinceMonthEnd) === 'completed'),
    [monthsData]
  )

  const periods = useMemo(() => groupRejectionPeriods(monthsData), [monthsData])
  const unflaggedMonths = useMemo(
    () =>
      [...monthsData].filter((m) => (m.issueTypes || []).length === 0).sort(
        (a, b) => b.conversionMonth.localeCompare(a.conversionMonth)
      ),
    [monthsData]
  )

  // Once data lands, anchor the selected month to the row's worst month —
  // unless the URL already pinned one (and that pin is still in range).
  useEffect(() => {
    if (monthsData.length === 0) return
    const inRange = (m: string | null) =>
      m != null && monthsData.some((row) => row.conversionMonth === m)
    if (inRange(selectedMonth)) return
    if (summary.worstMonth) {
      setSelectedMonth(summary.worstMonth)
    } else {
      // No worst month (no rate data at all) — fall back to most recent.
      const latest = monthsData[monthsData.length - 1]
      if (latest) setSelectedMonth(latest.conversionMonth)
    }
  }, [monthsData])

  // Maturity caveat: list past months whose totals are most likely still
  // rising (within ~90 days of month-end). Backend used to distinguish
  // completed vs. settled at 45d; without that gate we keep our own soft
  // window so the prose stays useful for recent months only.
  const completedMonths = useMemo(
    () =>
      monthsData.filter(
        (m) => m.daysSinceMonthEnd >= 0 && m.daysSinceMonthEnd < 90
      ),
    [monthsData]
  )

  const jumpToWorstMonth = () => {
    if (!summary.worstMonth) return
    setSelectedMonth(summary.worstMonth)
    setAdvancedOpen(true)
    // Defer the scroll one tick so the <details> has expanded and the row
    // exists in layout. requestAnimationFrame is enough; setTimeout 0 also
    // works.
    window.requestAnimationFrame(() => {
      const container = advancedContainerRef.current
      if (!container) return
      const row = container.querySelector(
        `tr[data-month="${summary.worstMonth}"]`
      ) as HTMLElement | null
      if (row) row.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }

  const availableDevices = useMemo(() => {
    // No device discovery endpoint for rejection — use a stable set.
    return ['Mobile', 'Desktop']
  }, [])

  return (
    <div className="anomalies-page anomalies-detail">
      <Header
        backPath="/anomalies?tab=rejection-review"
        backLabel="Back to rejection review"
      />

      <Loadable data={data}>
        <div className="anomalies-detail-headline text-dark text-bigger text-bolder m-t-2">
          {advertiserDomain}
        </div>

        <ControlBar
          months={months}
          onRangeChange={setMonths}
          device={device}
          devices={availableDevices}
          onDeviceChange={setDevice}
        />

        <div className="anomalies-detail-top-grid m-t-3">
          <StatusBanner
            advertiserDomain={advertiserDomain}
            issueTypes={headerIssueTypes}
            latestCompleted={latestCompleted}
            monthsCount={monthsData.length}
            worstMonth={summary.worstMonth}
            worstSaleRate={summary.worstSale}
            onJumpToWorst={summary.worstMonth ? jumpToWorstMonth : undefined}
          />
          <SummaryMatrix summary={summary} />
        </div>

        {monthsData.length === 0 ? (
          <div className="card m-t-3">
            <div className="card-body text-center text-light p-y-10">
              No rejection data for {advertiserDomain} in the selected range.
            </div>
          </div>
        ) : (
          <>
            <ProseSummary
              advertiserDomain={advertiserDomain}
              summary={summary}
              months={monthsData}
              completedMonths={completedMonths}
            />

            <div className="card m-t-3">
              <div className="card-body">
                <ChartTitle>Rejection rates over months</ChartTitle>
                <ResponsiveContainer width="99.9%" height={260}>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 8, left: 0, right: 16, bottom: 0 }}
                    onClick={(e: any) => {
                      const m = e?.activeLabel
                      if (m) setSelectedMonth(m)
                    }}
                  >
                    <CartesianGrid
                      horizontal
                      vertical={false}
                      strokeDasharray="6"
                      stroke="#EDEFF2"
                    />
                    <Tooltip content={<RatesTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
                    <ReferenceLine
                      y={0.15}
                      stroke="#EBB617"
                      strokeDasharray="4 4"
                      ifOverflow="extendDomain"
                      label={{
                        value: '15%',
                        position: 'right',
                        fill: '#EBB617',
                        fontSize: 10,
                      }}
                    />
                    <ReferenceLine
                      y={0.7}
                      stroke="#E55353"
                      strokeDasharray="4 4"
                      ifOverflow="extendDomain"
                      label={{
                        value: '70%',
                        position: 'right',
                        fill: '#E55353',
                        fontSize: 10,
                      }}
                    />
                    {selectedMonth && (
                      <ReferenceLine
                        x={selectedMonth}
                        stroke="#828B9E"
                        strokeDasharray="3 3"
                        ifOverflow="extendDomain"
                      />
                    )}
                    <Line
                      name="Rejected sale %"
                      dataKey="saleRate"
                      stroke={SALE_RATE_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5, style: { cursor: 'pointer' } }}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Line
                      name="Rejected conv. %"
                      dataKey="convRate"
                      stroke={CONV_RATE_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5, style: { cursor: 'pointer' } }}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatRejectionReviewMonth}
                      tick={{ fontSize: 11, fill: '#828B9E' }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      width={48}
                      tickFormatter={(v: number) => `${formatDecimal(v * 100, 0)}%`}
                      tick={{ fontSize: 11, fill: '#828B9E' }}
                      domain={[0, (max: number) => Math.max(max, 0.75) * 1.05]}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="m-t-3">
              <RejectionNetworkLane
                months={monthsData}
                networks={data?.networks}
                selectedMonth={selectedMonth}
                onSelectMonth={setSelectedMonth}
              />
            </div>

            <div className="anomalies-detail-charts-secondary m-t-3">
              <div className="card">
                <div className="card-body">
                  <ChartTitle>Monthly sale value</ChartTitle>
                  <ResponsiveContainer width="99.9%" height={220}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
                      onClick={(e: any) => {
                        const m = e?.activeLabel
                        if (m) setSelectedMonth(m)
                      }}
                    >
                      <CartesianGrid
                        horizontal
                        vertical={false}
                        strokeDasharray="6"
                        stroke="#EDEFF2"
                      />
                      <Tooltip content={<ValueTooltip kind="sale" />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {selectedMonth && (
                        <ReferenceLine
                          x={selectedMonth}
                          stroke="#828B9E"
                          strokeDasharray="3 3"
                          ifOverflow="extendDomain"
                        />
                      )}
                      <Bar
                        name="Total sale"
                        dataKey="totalSale"
                        isAnimationActive={false}
                      >
                        {chartData.map((d) => (
                          <Cell
                            key={d.month}
                            fill={TOTAL_BAR_COLOR}
                            fillOpacity={d.maturity === 'completed' ? 0.55 : 1}
                          />
                        ))}
                      </Bar>
                      <Bar
                        name="Rejected sale"
                        dataKey="rejectedSale"
                        isAnimationActive={false}
                      >
                        {chartData.map((d) => (
                          <Cell
                            key={d.month}
                            fill={REJECTED_BAR_COLOR}
                            fillOpacity={d.maturity === 'completed' ? 0.55 : 1}
                          />
                        ))}
                      </Bar>
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatRejectionReviewMonth}
                        tick={{ fontSize: 11, fill: '#828B9E' }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        width={60}
                        tickFormatter={(v: number) => formatDkkLarge(v)}
                        tick={{ fontSize: 11, fill: '#828B9E' }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  {completedMonths.length > 0 && (
                    <div className="anomalies-rejection-cooling-legend text-light text-small">
                      Lighter bars = still cooling (numbers may rise).
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <ChartTitle>Monthly conversions</ChartTitle>
                  <ResponsiveContainer width="99.9%" height={220}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
                      onClick={(e: any) => {
                        const m = e?.activeLabel
                        if (m) setSelectedMonth(m)
                      }}
                    >
                      <CartesianGrid
                        horizontal
                        vertical={false}
                        strokeDasharray="6"
                        stroke="#EDEFF2"
                      />
                      <Tooltip content={<ValueTooltip kind="conv" />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {selectedMonth && (
                        <ReferenceLine
                          x={selectedMonth}
                          stroke="#828B9E"
                          strokeDasharray="3 3"
                          ifOverflow="extendDomain"
                        />
                      )}
                      <Bar
                        name="Total conversions"
                        dataKey="totalConv"
                        isAnimationActive={false}
                      >
                        {chartData.map((d) => (
                          <Cell
                            key={d.month}
                            fill={TOTAL_BAR_COLOR}
                            fillOpacity={d.maturity === 'completed' ? 0.55 : 1}
                          />
                        ))}
                      </Bar>
                      <Bar
                        name="Rejected conversions"
                        dataKey="rejectedConv"
                        isAnimationActive={false}
                      >
                        {chartData.map((d) => (
                          <Cell
                            key={d.month}
                            fill={REJECTED_BAR_COLOR}
                            fillOpacity={d.maturity === 'completed' ? 0.55 : 1}
                          />
                        ))}
                      </Bar>
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatRejectionReviewMonth}
                        tick={{ fontSize: 11, fill: '#828B9E' }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        width={48}
                        tickFormatter={(v: number) =>
                          formatInteger(Math.round(v))
                        }
                        tick={{ fontSize: 11, fill: '#828B9E' }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  {completedMonths.length > 0 && (
                    <div className="anomalies-rejection-cooling-legend text-light text-small">
                      Lighter bars = still cooling (numbers may rise).
                    </div>
                  )}
                </div>
              </div>
            </div>

            <details
              className="anomalies-detail-advanced m-t-3"
              open={advancedOpen}
              onToggle={(e) =>
                setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)
              }
            >
              <summary>Show monthly breakdown</summary>
              <AdvancedSection
                periods={periods}
                unflaggedMonths={unflaggedMonths}
                selectedMonth={selectedMonth}
                onSelectMonth={setSelectedMonth}
                containerRef={advancedContainerRef}
                monthlySort={monthlySort}
              />
            </details>
          </>
        )}
      </Loadable>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Control bar
// ---------------------------------------------------------------------------
const ControlBar = (props: {
  months: number
  onRangeChange: (m: number) => void
  device: string
  devices: string[]
  onDeviceChange: (d: string) => void
}) => {
  return (
    <div className="anomalies-detail-controlbar m-t-2">
      <div className="anomalies-detail-controlbar-group">
        <span className="text-light text-small">Range:</span>
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={varClass({
              'anomalies-detail-range-btn': true,
              active: props.months === opt.key,
            })}
            onClick={() => props.onRangeChange(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="anomalies-detail-controlbar-group">
        <span className="text-light text-small">Device:</span>
        <button
          type="button"
          className={varClass({
            'anomalies-detail-range-btn': true,
            active: props.device === '',
          })}
          onClick={() => props.onDeviceChange('')}
        >
          All
        </button>
        {props.devices.map((d) => (
          <button
            key={d}
            type="button"
            className={varClass({
              'anomalies-detail-range-btn': true,
              active: props.device === d,
            })}
            onClick={() => props.onDeviceChange(d)}
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status banner (left card in top grid)
// ---------------------------------------------------------------------------
const StatusBanner = (props: {
  advertiserDomain: string
  issueTypes: TRejectionReviewIssueType[]
  latestCompleted: IRejectionReviewAdvertiserMonth | undefined
  monthsCount: number
  worstMonth: string | null
  worstSaleRate: number | null
  onJumpToWorst?: () => void
}) => {
  const investigating = props.issueTypes.length > 0
  return (
    <div
      className={varClass({
        'card anomalies-status-banner': true,
        'anomalies-status-banner-investigating': investigating,
        'anomalies-status-banner-noalert': !investigating,
      })}
    >
      <div className="card-body">
        <div className="text-light text-small text-uppercase text-bolder">
          {investigating
            ? 'Investigating rejection issues'
            : 'No active rejection issues'}
        </div>
        <div className="text-dark text-bigger text-bolder m-t-1">
          {props.advertiserDomain}
        </div>
        <div className="m-t-2 row row-center" style={{ gap: 6, flexWrap: 'wrap' }}>
          {props.issueTypes.length > 0 ? (
            <IssueTypeBadges issueTypes={props.issueTypes} />
          ) : (
            <span className="text-light text-small">
              Last {props.monthsCount} months show no flagged rejection
              activity.
            </span>
          )}
        </div>
        {props.worstMonth && props.onJumpToWorst && (
          <div className="text-light text-small m-t-2">
            Worst month:{' '}
            <button
              type="button"
              className="anomalies-rejection-banner-jump"
              onClick={props.onJumpToWorst}
            >
              <b>{formatRejectionReviewMonth(props.worstMonth)}</b>
              {' · '}
              <b>{formatRejectionPercent(props.worstSaleRate)}</b> sale rejected
              {' →'}
            </button>
          </div>
        )}
        {props.latestCompleted && (
          <div className="text-light text-small m-t-1">
            Latest completed month:{' '}
            <b className="text-dark">
              {formatRejectionReviewMonth(
                props.latestCompleted.conversionMonth
              )}
            </b>{' '}
            ·{' '}
            <b className="text-dark">
              {formatRejectionPercent(props.latestCompleted.rejectedSaleRate)}
            </b>{' '}
            sale rejected
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary matrix (right card in top grid)
// ---------------------------------------------------------------------------
const SummaryMatrix = (props: {
  summary: {
    totalRejectedSale: number
    totalRejectedConv: number
    totalSale: number
    totalConv: number
    worstSale: number | null
    worstConv: number | null
    worstMonth: string | null
    flaggedMonths: number
  }
}) => {
  const { summary } = props
  const overallSaleRate =
    summary.totalSale > 0 ? summary.totalRejectedSale / summary.totalSale : null
  const overallConvRate =
    summary.totalConv > 0 ? summary.totalRejectedConv / summary.totalConv : null
  return (
    <div className="card anomalies-health-matrix">
      <div className="card-body">
        <div className="text-light text-small text-uppercase text-bolder m-b-2">
          Rejection summary
        </div>
        <div className="anomalies-rejection-detail-kpis">
          <Kpi label="Rejected sale" value={formatDkkLarge(summary.totalRejectedSale)} />
          <Kpi label="Rejected conv." value={formatInteger(summary.totalRejectedConv)} />
          <Kpi
            label="Rejected sale %"
            value={formatRejectionPercent(overallSaleRate)}
          />
          <Kpi
            label="Rejected conv. %"
            value={formatRejectionPercent(overallConvRate)}
          />
          <Kpi
            label="Worst sale %"
            value={formatRejectionPercent(summary.worstSale)}
            sub={summary.worstMonth ? formatRejectionReviewMonth(summary.worstMonth) : undefined}
          />
          <Kpi
            label="Flagged months"
            value={formatInteger(summary.flaggedMonths)}
          />
        </div>
      </div>
    </div>
  )
}

const Kpi = (props: { label: string; value: string; sub?: string }) => (
  <div className="anomalies-rejection-detail-kpi">
    <div className="text-light text-small">{props.label}</div>
    <div className="text-dark text-bigger text-bolder">{props.value}</div>
    {props.sub && <div className="text-light text-small">{props.sub}</div>}
  </div>
)

// ---------------------------------------------------------------------------
// Prose summary — sentence-style narrative, similar to MetricEvidenceCards
// on the anomaly detail page.
// ---------------------------------------------------------------------------
const ProseSummary = (props: {
  advertiserDomain: string
  summary: {
    totalRejectedSale: number
    totalRejectedConv: number
    totalSale: number
    totalConv: number
    worstSale: number | null
    worstConv: number | null
    worstMonth: string | null
    flaggedMonths: number
  }
  months: IRejectionReviewAdvertiserMonth[]
  completedMonths: IRejectionReviewAdvertiserMonth[]
}) => {
  const { summary, months, advertiserDomain, completedMonths } = props
  const flaggedList = months.filter((m) => (m.issueTypes || []).length > 0)
  const latestFlagged = flaggedList.length > 0 ? flaggedList[flaggedList.length - 1] : null

  const completedLabel = completedMonths
    .map((m) => formatRejectionReviewMonth(m.conversionMonth))
    .join(', ')

  return (
    <div className="card m-t-3 anomalies-rejection-detail-prose">
      <div className="card-body">
        <div className="text-light text-small text-uppercase text-bolder m-b-1">
          Summary
        </div>
        <div className="text-dark">
          Across the last {months.length} months,{' '}
          <b>{advertiserDomain}</b> rejected{' '}
          <b>{formatDkkLarge(summary.totalRejectedSale)}</b> of sale value
          across <b>{formatInteger(summary.totalRejectedConv)}</b> conversions.
          {summary.worstMonth && summary.worstSale != null && (
            <>
              {' '}The worst month was{' '}
              <b>{formatRejectionReviewMonth(summary.worstMonth)}</b> at{' '}
              <b>{formatRejectionPercent(summary.worstSale)}</b> sale rejected.
            </>
          )}
          {summary.flaggedMonths > 0 ? (
            <>
              {' '}
              <b>{summary.flaggedMonths}</b> month
              {summary.flaggedMonths === 1 ? ' is' : 's are'} currently flagged.
              {latestFlagged?.reasonText && (
                <>
                  {' '}Latest:{' '}
                  <span className="text-light">
                    {europeanizeNumbers(latestFlagged.reasonText)}
                  </span>
                </>
              )}
            </>
          ) : (
            <> No months in this window are currently flagged.</>
          )}
          {completedMonths.length > 0 && (
            <div className="text-light text-small m-t-2">
              Numbers for <b>{completedLabel}</b> may still rise — rejections
              can land weeks after the conversion month, and recent months
              are still actively accumulating.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Misc bits
// ---------------------------------------------------------------------------
const ChartTitle = (props: { children: React.ReactNode }) => (
  <div className="text-dark text-bold m-b-2">{props.children}</div>
)

const RatesTooltip = (props: any) => {
  const { active, payload, label } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? formatRejectionReviewMonth(label) : ''}
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: SALE_RATE_COLOR }}
        />
        <span className="anomalies-chart-tooltip-label">Rejected sale %</span>
        <span className="anomalies-chart-tooltip-value">
          {formatRejectionPercent(row.saleRate)}
        </span>
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: CONV_RATE_COLOR }}
        />
        <span className="anomalies-chart-tooltip-label">Rejected conv. %</span>
        <span className="anomalies-chart-tooltip-value">
          {formatRejectionPercent(row.convRate)}
        </span>
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span className="anomalies-chart-tooltip-label">Maturity</span>
        <span className="anomalies-chart-tooltip-value">{row.maturity}</span>
      </div>
    </div>
  )
}

const ValueTooltip = (props: any) => {
  const { active, payload, label, kind } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  const isSale = kind === 'sale'
  const total = isSale ? row.totalSale : row.totalConv
  const rejected = isSale ? row.rejectedSale : row.rejectedConv
  const fmt = isSale
    ? (v: number) => formatDkkLarge(v)
    : (v: number) => formatInteger(Math.round(v))
  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? formatRejectionReviewMonth(label) : ''}
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: TOTAL_BAR_COLOR }}
        />
        <span className="anomalies-chart-tooltip-label">
          {isSale ? 'Total sale' : 'Total conv.'}
        </span>
        <span className="anomalies-chart-tooltip-value">{fmt(total)}</span>
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: REJECTED_BAR_COLOR }}
        />
        <span className="anomalies-chart-tooltip-label">
          {isSale ? 'Rejected sale' : 'Rejected conv.'}
        </span>
        <span className="anomalies-chart-tooltip-value">{fmt(rejected)}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Advanced section — flagged periods first, then a sortable "Other months"
// table for the unflagged remainder. Click any row to set selectedMonth.
// ---------------------------------------------------------------------------
const AdvancedSection = (props: {
  periods: RejectionPeriod[]
  unflaggedMonths: IRejectionReviewAdvertiserMonth[]
  selectedMonth: string | null
  onSelectMonth: (m: string) => void
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  monthlySort: {
    sort: string
    dir: 'asc' | 'desc'
    toggleSort: (field: string, defaultDir: 'asc' | 'desc') => () => void
  }
}) => {
  const { periods, unflaggedMonths, selectedMonth, onSelectMonth, containerRef, monthlySort } = props

  const sortedUnflagged = useMemo(() => {
    const { sort, dir } = monthlySort
    const sign = dir === 'desc' ? -1 : 1
    return [...unflaggedMonths].sort((a, b) => {
      const av = (a as any)[sort]
      const bv = (b as any)[sort]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign
      return String(av).localeCompare(String(bv)) * sign
    })
  }, [unflaggedMonths, monthlySort.sort, monthlySort.dir])

  return (
    <div className="card m-t-2">
      <div className="card-body" ref={containerRef}>
        {periods.length === 0 ? (
          <div className="text-light text-small m-b-3">
            No flagged periods in the selected range.
          </div>
        ) : (
          <>
            <div className="text-dark text-bold m-b-2">Flagged periods</div>
            <div className="anomalies-rejection-periods">
              {periods.map((p) => (
                <PeriodCard
                  key={p.startMonth}
                  period={p}
                  selectedMonth={selectedMonth}
                  onSelectMonth={onSelectMonth}
                />
              ))}
            </div>
          </>
        )}

        {sortedUnflagged.length > 0 && (
          <details className="m-t-3">
            <summary className="text-dark text-bold anomalies-rejection-other-months-toggle">
              Other months ({sortedUnflagged.length})
            </summary>
            <div className="table-container-overflow m-t-2">
              <table className="table table-bordered table-sortable anomalies-advanced-table">
                <thead>
                  <tr>
                    <TableHead value="conversionMonth" label="Month" desc {...monthlySort} />
                    <TableHead value="totalSaleDkk" label="Total sale" align="right" desc {...monthlySort} />
                    <TableHead value="rejectedSaleDkk" label="Rejected sale" align="right" desc {...monthlySort} />
                    <TableHead value="rejectedSaleRate" label="Rej. sale %" align="right" desc {...monthlySort} />
                    <TableHead value="totalConversions" label="Total conv." align="right" desc {...monthlySort} />
                    <TableHead value="rejectedConversions" label="Rej. conv." align="right" desc {...monthlySort} />
                    <TableHead value="rejectedConversionRate" label="Rej. conv. %" align="right" desc {...monthlySort} />
                  </tr>
                </thead>
                <tbody>
                  {sortedUnflagged.map((m) => (
                    <MonthRow
                      key={m.conversionMonth}
                      month={m}
                      selected={selectedMonth === m.conversionMonth}
                      onSelectMonth={onSelectMonth}
                      showReason={false}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </div>
  )
}

const PeriodCard = (props: {
  period: RejectionPeriod
  selectedMonth: string | null
  onSelectMonth: (m: string) => void
}) => {
  const { period, selectedMonth, onSelectMonth } = props
  const range =
    period.startMonth === period.endMonth
      ? formatRejectionReviewMonth(period.startMonth)
      : `${formatRejectionReviewMonth(period.startMonth)} – ${formatRejectionReviewMonth(period.endMonth)}`
  return (
    <details className="anomalies-rejection-period" open>
      <summary className="anomalies-rejection-period-summary">
        <span className="anomalies-rejection-period-range text-dark text-bolder">
          {range}
        </span>
        <span className="text-light text-small">
          · {period.monthCount} month{period.monthCount === 1 ? '' : 's'} flagged ·{' '}
          <b className="text-dark">{formatDkkLarge(period.totalRejectedSaleDkk)}</b>{' '}
          rejected sale ·{' '}
          <b className="text-dark">{formatInteger(period.totalRejectedConversions)}</b>{' '}
          rejected conv.
        </span>
        <span className="anomalies-rejection-period-types">
          <IssueTypeBadges issueTypes={period.issueTypes} />
        </span>
      </summary>
      <div className="table-container-overflow m-t-2">
        <table className="table table-bordered anomalies-advanced-table">
          <thead>
            <tr>
              <th>Month</th>
              <th className="text-right">Total sale</th>
              <th className="text-right">Rejected sale</th>
              <th className="text-right">Rej. sale %</th>
              <th className="text-right">Total conv.</th>
              <th className="text-right">Rej. conv.</th>
              <th className="text-right">Rej. conv. %</th>
              <th>Issues</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {period.months.map((m) => (
              <MonthRow
                key={m.conversionMonth}
                month={m}
                selected={selectedMonth === m.conversionMonth}
                onSelectMonth={onSelectMonth}
                showReason
              />
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

const MonthRow = (props: {
  month: IRejectionReviewAdvertiserMonth
  selected: boolean
  onSelectMonth: (m: string) => void
  showReason?: boolean
}) => {
  const { month: m, selected, onSelectMonth, showReason } = props
  return (
    <tr
      data-month={m.conversionMonth}
      className={varClass({
        'anomalies-rejection-row-clickable': true,
        'anomalies-rejection-row-highlighted': selected,
      })}
      onClick={() => onSelectMonth(m.conversionMonth)}
    >
      <td className="text-dark text-bold">
        <span className="row row-center" style={{ gap: 6 }}>
          {formatRejectionReviewMonth(m.conversionMonth)}
          {deriveMaturity(m.daysSinceMonthEnd) === 'open' && (
            <OpenMonthBadge />
          )}
        </span>
      </td>
      <td className="text-right text-light">
        {formatRejectionDkk(m.totalSaleDkk)}
      </td>
      <td className="text-right">{formatRejectionDkk(m.rejectedSaleDkk)}</td>
      <td className="text-right text-bold">
        {formatRejectionPercent(m.rejectedSaleRate)}
      </td>
      <td className="text-right text-light">
        {formatInteger(m.totalConversions)}
      </td>
      <td className="text-right">{formatInteger(m.rejectedConversions)}</td>
      <td className="text-right">
        {formatRejectionPercent(m.rejectedConversionRate)}
      </td>
      {showReason !== false && (
        <>
          <td>
            <IssueTypeBadges issueTypes={m.issueTypes} />
          </td>
          <td className="text-light text-small">
            {m.reasonText ? europeanizeNumbers(m.reasonText) : '—'}
          </td>
        </>
      )}
    </tr>
  )
}
